import { VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { buttonVariants } from '@/ui/shared/buttonVariants';
import { Link } from '../Link';

type IconPosition = 'left' | 'right';
type IconProps =
  | {
      /** 表示するアイコン */
      icon: ReactNode;
      /** アイコンの表示位置 */
      iconPosition: IconPosition;
    }
  | {
      /** 表示するアイコン */
      icon?: undefined;
      /** アイコンの表示位置 */
      iconPosition?: undefined;
    };

type Props = React.ComponentProps<typeof Link> & VariantProps<typeof buttonVariants> & IconProps;

/**
 * ボタンスタイルのリンクコンポーネント
 *
 * LinkコンポーネントにButtonコンポーネントと同じスタイルを適用したコンポーネントです。
 * 内部リンクと外部リンクの両方をサポートし、Buttonコンポーネントと同じvariant、size、fullWidthプロパティを使用できます。
 *
 * アイコンの表示位置を指定することで、テキストの左側または右側にアイコンを表示できます。
 * iconとiconPositionは同時に指定するか、両方とも未指定にする必要があります。
 *
 * @example
 * ```tsx
 * // 基本的な内部リンク
 * <ButtonLink to="/dashboard" variant="primary">
 *   ダッシュボードへ
 * </ButtonLink>
 *
 * // アイコン付きの外部リンク
 * <ButtonLink
 *   to="https://example.com"
 *   isExternal
 *   variant="outline"
 *   icon={<PlusIcon size='xs' />}
 *   iconPosition="left"
 * >
 *   外部サイトへ
 * </ButtonLink>
 *
 * // フル幅のボタンリンク
 * <ButtonLink
 *   to="/settings"
 *   variant="secondary"
 *   fullWidth
 * >
 *   設定
 * </ButtonLink>
 * ```
 */
export const ButtonLink: React.FC<Props> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconPosition,
  children,
  ...props
}) => {
  // classNameを除外してLinkコンポーネントに渡す
  const { className, ...linkProps } = props;

  return (
    <Link className={buttonVariants({ variant, size, fullWidth })} {...linkProps}>
      {icon && iconPosition === 'left' && icon}
      {children}
      {icon && iconPosition === 'right' && icon}
    </Link>
  );
};
